export type WhiteLabelFontTheme = {
  sans: string;
};

export type WhiteLabelColorTheme = {
  canvas: string;
  canvasMuted: string;
  surface: string;
  surfaceSubtle: string;
  surfaceMuted: string;
  surfaceSoft: string;
  primary: string;
  primaryAlt: string;
  primaryHover: string;
  primarySoft: string;
  accent: string;
  accentHover: string;
  accentStrong: string;
  accentSoft: string;
  text: string;
  textMuted: string;
  textSoft: string;
  textPlaceholder: string;
  textInverse: string;
  border: string;
  borderStrong: string;
  borderLight: string;
  overlay: string;
  success: string;
  successSoft: string;
  successSoftAlt: string;
  successPale: string;
  successBright: string;
  successHero: string;
  successStrong: string;
  successDeep: string;
  successBorderSoft: string;
  successBrightSoft: string;
  successTextStrong: string;
  warning: string;
  warningSoft: string;
  warningBright: string;
  warningPale: string;
  warningBrightSoft: string;
  warningTextStrong: string;
  warningAlt: string;
  warningHero: string;
  danger: string;
  dangerSoft: string;
  dangerBright: string;
  dangerHero: string;
  dangerBrightSoft: string;
  dangerTextStrong: string;
  info: string;
  infoSoft: string;
  infoBright: string;
  infoSoftAlt: string;
  infoTextStrong: string;
  infoAccent: string;
  purpleSoft: string;
  purplePale: string;
  purpleStrong: string;
  purpleText: string;
  orangeSoft: string;
  orangeText: string;
  shadow: string;
};

export type WhiteLabelGradientTheme = {
  primary: {
    start: string;
    end: string;
  };
  accent: {
    start: string;
    end: string;
  };
  step: {
    start: string;
    end: string;
  };
};

export type WhiteLabelComponentClasses = {
  button: {
    base: string;
    solid: string;
    solidBgColor: string;
    solidTextColor: string;
    outline: string;
    outlineBorderColor: string;
    outlineTextColor: string;
  };
  card: {
    base: string;
    bgColor: string;
    borderColor: string;
    textColor: string;
    mutedBgColor: string;
    softBgColor: string;
    titleColor: string;
    subtitleColor: string;
  };
  dashboardCard: {
    base: string;
    bgColor: string;
    borderColor: string;
    textColor: string;
    mutedTextColor: string;
    accentBgColor: string;
    accentTextColor: string;
  };
  input: {
    base: string;
    bgColor: string;
    borderColor: string;
    textColor: string;
    placeholderColor: string;
    labelColor: string;
    focusBorderColor: string;
    focusRingColor: string;
    errorBorderColor: string;
    errorTextColor: string;
  };
  select: {
    trigger: string;
    bgColor: string;
    borderColor: string;
    textColor: string;
    placeholderColor: string;
    menu: string;
    option: string;
    optionHoverBgColor: string;
  };
  nav: {
    linkColor: string;
    linkHoverColor: string;
    menuBgColor: string;
    menuBorderColor: string;
    menuItemColor: string;
    menuItemHoverBgColor: string;
  };
  footer: {
    root: string;
    bgColor: string;
    textColor: string;
    brandTextColor: string;
    socialBgColor: string;
    socialTextColor: string;
    borderColor: string;
  };
  modal: {
    overlayBgColor: string;
    panelBgColor: string;
    panelTextColor: string;
    panelBorderColor: string;
    titleColor: string;
    closeButtonColor: string;
    bodyTextColor: string;
  };
  menu: {
    panelBgColor: string;
    panelBorderColor: string;
    panelTextColor: string;
    headerBgColor: string;
    headerTextColor: string;
    itemTextColor: string;
    itemHoverBgColor: string;
  };
  avatar: {
    buttonBgColor: string;
    buttonBorderColor: string;
    buttonTextColor: string;
    badgeBgColor: string;
    badgeTextColor: string;
    iconColor: string;
  };
  table: {
    headerBgColor: string;
    headerTextColor: string;
    rowBgColor: string;
    rowTextColor: string;
    borderColor: string;
    controlBgColor: string;
    controlTextColor: string;
    controlBorderColor: string;
    accentBgColor: string;
    accentTextColor: string;
  };
  header: {
    root: string;
    bgColor: string;
    textColor: string;
    brandTextColor: string;
    phoneTextColor: string;
    menuIconColor: string;
    loginButton: string;
    loginButtonBgColor: string;
    loginButtonTextColor: string;
  };
  sidebar: {
    root: string;
    bgColor: string;
    textColor: string;
    brandTextColor: string;
    activeItem: string;
    activeItemBgColor: string;
    activeItemTextColor: string;
    inactiveItem: string;
    inactiveItemTextColor: string;
    toggleButton: string;
    toggleButtonBgColor: string;
    toggleIconColor: string;
  };
  summary: {
    cardBgColor: string;
    cardBorderColor: string;
    titleColor: string;
    labelColor: string;
    valueColor: string;
    actionColor: string;
    dividerColor: string;
  };
  addOns: {
    cardBgColor: string;
    cardBorderColor: string;
    titleColor: string;
    textColor: string;
    actionColor: string;
    checkboxColor: string;
  };
  uploadCard: {
    borderColor: string;
    activeBorderColor: string;
    bgColor: string;
    activeBgColor: string;
    overlayBgColor: string;
    titleColor: string;
    textColor: string;
    accentColor: string;
    inverseTextColor: string;
  };
  formSection: {
    titleColor: string;
    labelColor: string;
    helperTextColor: string;
    errorTextColor: string;
  };
  pageShell: {
    canvasBgColor: string;
    sectionBgColor: string;
    surfaceBgColor: string;
    textColor: string;
    mutedTextColor: string;
    borderColor: string;
  };
  quoteFooter: {
    primaryButtonBgColor: string;
    primaryButtonTextColor: string;
    secondaryButtonBorderColor: string;
    secondaryButtonTextColor: string;
  };
  loginFormBackground: {
    bgColor: any;
  };
  progressIndicator: {
    wrapperClassName: string;
    labelClassName: string;
  };
};

export type WhiteLabelTheme = {
  font: WhiteLabelFontTheme;
  componentClasses: WhiteLabelComponentClasses;
  colors: WhiteLabelColorTheme;
  gradients: WhiteLabelGradientTheme;
};

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends Record<string, unknown> ? DeepPartial<T[K]> : T[K];
};

type WhiteLabelComponentGroupName = keyof WhiteLabelComponentClasses;

export type WhiteLabelThemeInput = {
  font: WhiteLabelFontTheme;
  colors: WhiteLabelColorTheme;
  gradients: WhiteLabelGradientTheme;
  componentClasses?: DeepPartial<WhiteLabelComponentClasses>;
};

export const defaultWhiteLabelComponentClasses: WhiteLabelComponentClasses = {
  button: {
    base: "",
    solid: "",
    solidBgColor: "",
    solidTextColor: "",
    outline: "",
    outlineBorderColor: "",
    outlineTextColor: "",
  },
  card: {
    base: "",
    bgColor: "",
    borderColor: "",
    textColor: "",
    mutedBgColor: "",
    softBgColor: "",
    titleColor: "",
    subtitleColor: "",
  },
  dashboardCard: {
    base: "",
    bgColor: "",
    borderColor: "",
    textColor: "",
    mutedTextColor: "",
    accentBgColor: "",
    accentTextColor: "",
  },
  input: {
    base: "",
    bgColor: "",
    borderColor: "",
    textColor: "",
    placeholderColor: "",
    labelColor: "",
    focusBorderColor: "",
    focusRingColor: "",
    errorBorderColor: "",
    errorTextColor: "",
  },
  select: {
    trigger: "",
    bgColor: "",
    borderColor: "",
    textColor: "",
    placeholderColor: "",
    menu: "",
    option: "",
    optionHoverBgColor: "",
  },
  nav: {
    linkColor: "",
    linkHoverColor: "",
    menuBgColor: "",
    menuBorderColor: "",
    menuItemColor: "",
    menuItemHoverBgColor: "",
  },
  footer: {
    root: "",
    bgColor: "",
    textColor: "",
    brandTextColor: "",
    socialBgColor: "",
    socialTextColor: "",
    borderColor: "",
  },
  modal: {
    overlayBgColor: "",
    panelBgColor: "",
    panelTextColor: "",
    panelBorderColor: "",
    titleColor: "",
    closeButtonColor: "",
    bodyTextColor: "",
  },
  menu: {
    panelBgColor: "",
    panelBorderColor: "",
    panelTextColor: "",
    headerBgColor: "",
    headerTextColor: "",
    itemTextColor: "",
    itemHoverBgColor: "",
  },
  avatar: {
    buttonBgColor: "",
    buttonBorderColor: "",
    buttonTextColor: "",
    badgeBgColor: "",
    badgeTextColor: "",
    iconColor: "",
  },
  table: {
    headerBgColor: "",
    headerTextColor: "",
    rowBgColor: "",
    rowTextColor: "",
    borderColor: "",
    controlBgColor: "",
    controlTextColor: "",
    controlBorderColor: "",
    accentBgColor: "",
    accentTextColor: "",
  },
  header: {
    root: "",
    bgColor: "",
    textColor: "",
    brandTextColor: "",
    phoneTextColor: "",
    menuIconColor: "",
    loginButton: "",
    loginButtonBgColor: "",
    loginButtonTextColor: "",
  },
  sidebar: {
    root: "",
    bgColor: "",
    textColor: "",
    brandTextColor: "",
    activeItem: "",
    activeItemBgColor: "",
    activeItemTextColor: "",
    inactiveItem: "",
    inactiveItemTextColor: "",
    toggleButton: "",
    toggleButtonBgColor: "",
    toggleIconColor: "",
  },
  summary: {
    cardBgColor: "",
    cardBorderColor: "",
    titleColor: "",
    labelColor: "",
    valueColor: "",
    actionColor: "",
    dividerColor: "",
  },
  addOns: {
    cardBgColor: "",
    cardBorderColor: "",
    titleColor: "",
    textColor: "",
    actionColor: "",
    checkboxColor: "",
  },
  uploadCard: {
    borderColor: "",
    activeBorderColor: "",
    bgColor: "",
    activeBgColor: "",
    overlayBgColor: "",
    titleColor: "",
    textColor: "",
    accentColor: "",
    inverseTextColor: "",
  },
  formSection: {
    titleColor: "",
    labelColor: "",
    helperTextColor: "",
    errorTextColor: "",
  },
  pageShell: {
    canvasBgColor: "",
    sectionBgColor: "",
    surfaceBgColor: "",
    textColor: "",
    mutedTextColor: "",
    borderColor: "",
  },
  quoteFooter: {
    primaryButtonBgColor: "",
    primaryButtonTextColor: "",
    secondaryButtonBorderColor: "",
    secondaryButtonTextColor: "",
  },
  loginFormBackground: {
    bgColor: "",
  },
  progressIndicator: {
    wrapperClassName: "",
    labelClassName: "",
  },
};

export function createWhiteLabelTheme(input: WhiteLabelThemeInput): WhiteLabelTheme {
  return {
    font: input.font,
    colors: input.colors,
    gradients: input.gradients,
    componentClasses: mergeComponentClasses(input.componentClasses),
  };
}

function mergeComponentClasses(
  overrides?: DeepPartial<WhiteLabelComponentClasses>
): WhiteLabelComponentClasses {
  const groupNames = Object.keys(
    defaultWhiteLabelComponentClasses
  ) as WhiteLabelComponentGroupName[];

  return Object.fromEntries(
    groupNames.map((groupName) => [
      groupName,
      mergeComponentGroup(groupName, overrides?.[groupName]),
    ])
  ) as WhiteLabelComponentClasses;
}

function mergeComponentGroup<K extends WhiteLabelComponentGroupName>(
  groupName: K,
  overrides?: DeepPartial<WhiteLabelComponentClasses[K]>
): WhiteLabelComponentClasses[K] {
  return {
    ...defaultWhiteLabelComponentClasses[groupName],
    ...overrides,
  };
}

export function applyWhiteLabelThemeToDocument(theme: WhiteLabelTheme) {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const { font, colors, gradients } = theme;

  root.style.setProperty("--theme-font-sans", font.sans);
  root.style.setProperty("--theme-color-canvas", colors.canvas);
  root.style.setProperty("--theme-color-canvas-muted", colors.canvasMuted);
  root.style.setProperty("--theme-color-surface", colors.surface);
  root.style.setProperty("--theme-color-surface-subtle", colors.surfaceSubtle);
  root.style.setProperty("--theme-color-surface-muted", colors.surfaceMuted);
  root.style.setProperty("--theme-color-surface-soft", colors.surfaceSoft);
  root.style.setProperty("--theme-color-primary", colors.primary);
  root.style.setProperty("--theme-color-primary-alt", colors.primaryAlt);
  root.style.setProperty("--theme-color-primary-hover", colors.primaryHover);
  root.style.setProperty("--theme-color-primary-soft", colors.primarySoft);
  root.style.setProperty("--theme-color-accent", colors.accent);
  root.style.setProperty("--theme-color-accent-hover", colors.accentHover);
  root.style.setProperty("--theme-color-accent-strong", colors.accentStrong);
  root.style.setProperty("--theme-color-accent-soft", colors.accentSoft);
  root.style.setProperty("--theme-color-text", colors.text);
  root.style.setProperty("--theme-color-text-muted", colors.textMuted);
  root.style.setProperty("--theme-color-text-soft", colors.textSoft);
  root.style.setProperty("--theme-color-text-placeholder", colors.textPlaceholder);
  root.style.setProperty("--theme-color-text-inverse", colors.textInverse);
  root.style.setProperty("--theme-color-border", colors.border);
  root.style.setProperty("--theme-color-border-strong", colors.borderStrong);
  root.style.setProperty("--theme-color-border-light", colors.borderLight);
  root.style.setProperty("--theme-color-overlay", colors.overlay);
  root.style.setProperty("--theme-color-success", colors.success);
  root.style.setProperty("--theme-color-success-soft", colors.successSoft);
  root.style.setProperty("--theme-color-success-soft-alt", colors.successSoftAlt);
  root.style.setProperty("--theme-color-success-pale", colors.successPale);
  root.style.setProperty("--theme-color-success-bright", colors.successBright);
  root.style.setProperty("--theme-color-success-hero", colors.successHero);
  root.style.setProperty("--theme-color-success-strong", colors.successStrong);
  root.style.setProperty("--theme-color-success-deep", colors.successDeep);
  root.style.setProperty("--theme-color-success-border-soft", colors.successBorderSoft);
  root.style.setProperty("--theme-color-success-bright-soft", colors.successBrightSoft);
  root.style.setProperty("--theme-color-success-text-strong", colors.successTextStrong);
  root.style.setProperty("--theme-color-warning", colors.warning);
  root.style.setProperty("--theme-color-warning-soft", colors.warningSoft);
  root.style.setProperty("--theme-color-warning-bright", colors.warningBright);
  root.style.setProperty("--theme-color-warning-pale", colors.warningPale);
  root.style.setProperty("--theme-color-warning-bright-soft", colors.warningBrightSoft);
  root.style.setProperty("--theme-color-warning-text-strong", colors.warningTextStrong);
  root.style.setProperty("--theme-color-warning-alt", colors.warningAlt);
  root.style.setProperty("--theme-color-warning-hero", colors.warningHero);
  root.style.setProperty("--theme-color-danger", colors.danger);
  root.style.setProperty("--theme-color-danger-soft", colors.dangerSoft);
  root.style.setProperty("--theme-color-danger-bright", colors.dangerBright);
  root.style.setProperty("--theme-color-danger-hero", colors.dangerHero);
  root.style.setProperty("--theme-color-danger-bright-soft", colors.dangerBrightSoft);
  root.style.setProperty("--theme-color-danger-text-strong", colors.dangerTextStrong);
  root.style.setProperty("--theme-color-info", colors.info);
  root.style.setProperty("--theme-color-info-soft", colors.infoSoft);
  root.style.setProperty("--theme-color-info-bright", colors.infoBright);
  root.style.setProperty("--theme-color-info-soft-alt", colors.infoSoftAlt);
  root.style.setProperty("--theme-color-info-text-strong", colors.infoTextStrong);
  root.style.setProperty("--theme-color-info-accent", colors.infoAccent);
  root.style.setProperty("--theme-color-purple-soft", colors.purpleSoft);
  root.style.setProperty("--theme-color-purple-pale", colors.purplePale);
  root.style.setProperty("--theme-color-purple-strong", colors.purpleStrong);
  root.style.setProperty("--theme-color-purple-text", colors.purpleText);
  root.style.setProperty("--theme-color-orange-soft", colors.orangeSoft);
  root.style.setProperty("--theme-color-orange-text", colors.orangeText);
  root.style.setProperty("--theme-color-shadow", colors.shadow);
  root.style.setProperty(
    "--theme-gradient-primary",
    `linear-gradient(350deg, ${gradients.primary.start}, ${gradients.primary.end})`
  );
  root.style.setProperty(
    "--theme-gradient-accent",
    `linear-gradient(90deg, ${gradients.accent.start}, ${gradients.accent.end})`
  );
  root.style.setProperty(
    "--theme-gradient-step",
    `linear-gradient(90deg, ${gradients.step.start}, ${gradients.step.end})`
  );
}
